/** Distances and areas on the map, for the draft bar's running measurement. */

export type LatLng = [number, number]

const EARTH_RADIUS = 6_371_008.8

const rad = (degrees: number) => (degrees * Math.PI) / 180

/** Great-circle distance in metres. Within centimetres of the ellipsoid at a field's scale. */
export function distance(a: LatLng, b: LatLng): number {
  const dLat = rad(b[0] - a[0])
  const dLon = rad(b[1] - a[1])
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[0])) * Math.cos(rad(b[0])) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_RADIUS * Math.asin(Math.min(1, Math.sqrt(h)))
}

export function pathLength(points: LatLng[]): number {
  let total = 0
  for (let i = 1; i < points.length; i++) total += distance(points[i - 1], points[i])
  return total
}

/**
 * Area of a ring in square metres, on a local flat projection around its centre.
 *
 * Accurate to well under a percent for anything a game is played on; a spherical-excess formula
 * would only matter for areas the size of a region.
 */
export function area(ring: LatLng[]): number {
  if (ring.length < 3) return 0
  const lat0 = ring.reduce((sum, p) => sum + p[0], 0) / ring.length
  const kx = rad(1) * EARTH_RADIUS * Math.cos(rad(lat0))
  const ky = rad(1) * EARTH_RADIUS
  let sum = 0
  for (let i = 0; i < ring.length; i++) {
    const [lat1, lon1] = ring[i]
    const [lat2, lon2] = ring[(i + 1) % ring.length]
    sum += lon1 * kx * (lat2 * ky) - lon2 * kx * (lat1 * ky)
  }
  return Math.abs(sum) / 2
}

/** The four corners of the box two opposite corners describe, as the server stores a rectangle. */
export function boxCorners(a: LatLng, b: LatLng): LatLng[] {
  return [
    [a[0], a[1]],
    [a[0], b[1]],
    [b[0], b[1]],
    [b[0], a[1]],
  ]
}

export function formatDistance(metres: number): string {
  if (metres >= 1000) return `${(metres / 1000).toFixed(metres >= 10_000 ? 1 : 2)} km`
  return `${Math.round(metres)} m`
}

export function formatArea(squareMetres: number): string {
  if (squareMetres >= 10_000) return `${(squareMetres / 10_000).toFixed(2)} ha`
  return `${Math.round(squareMetres)} m²`
}
